export {};

const RECENTLY_VIEWED_STORAGE_KEY = 'recently_viewed:items';
const TRACK_LIMIT = 20;

/**
 * @typedef {HTMLElement & {
 *   recentlyViewedUpdateControls?: () => void,
 *   recentlyViewedCleanupControls?: () => void
 * }} RecentlyViewedSection
 */

/** @returns {string[]} */
const readHandles = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch (error) {
    console.warn('Failed to parse recently viewed products', error);
    return [];
  }
};

/** @param {string[]} handles */
const writeHandles = (handles) => {
  localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(handles));
};

/**
 * @param {string | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
const parseNumberAttr = (value, fallback) => {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

/**
 * @param {string} markup
 * @returns {Element | null}
 */
const parseCard = (markup) => {
  const doc = new DOMParser().parseFromString(markup, 'text/html');
  return doc.querySelector('[data-recently-viewed-card]');
};

/**
 * @param {string} handle
 * @returns {Promise<Element>}
 */
const fetchCard = async (handle) => {
  const shopifyConfig = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify);
  const rootPath = shopifyConfig?.routes?.root || '/';
  const url = new URL(`${rootPath}products/${encodeURIComponent(handle)}`, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);
  for (const [key, value] of currentParams.entries()) {
    url.searchParams.set(key, value);
  }
  url.searchParams.set('section_id', 'recently-viewed-product-card');

  const response = await fetch(url.toString(), { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Failed to fetch recently viewed card for ${handle}`);

  const card = parseCard(await response.text());
  if (!card) throw new Error(`No card markup found for ${handle}`);

  return card;
};

const SCROLL_TOLERANCE = 2;
const MIN_LINE_THUMB_WIDTH = 14;

/**
 * @param {HTMLElement} grid
 * @returns {number}
 */
const getScrollStep = (grid) => {
  const firstCard = grid.querySelector('.resource-list__item');
  if (firstCard instanceof HTMLElement) {
    const styles = window.getComputedStyle(grid);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return firstCard.getBoundingClientRect().width + gap;
  }

  return Math.max(grid.clientWidth * 0.85, 1);
};

/**
 * @param {RecentlyViewedSection} section
 * @param {HTMLElement} grid
 * @returns {void}
 */
const setupControls = (section, grid) => {
  if (typeof section.recentlyViewedCleanupControls === 'function') {
    section.recentlyViewedCleanupControls();
  }

  const controls = section.querySelector('[data-recently-viewed-controls]');
  const progressLine = section.querySelector('[data-recently-viewed-line]');
  const progressThumb = section.querySelector('[data-recently-viewed-line-thumb]');
  const prevButton = section.querySelector('[data-recently-viewed-prev]');
  const nextButton = section.querySelector('[data-recently-viewed-next]');

  if (!(controls instanceof HTMLElement)) return;
  if (!(progressLine instanceof HTMLElement)) return;
  if (!(progressThumb instanceof HTMLElement)) return;
  if (!(prevButton instanceof HTMLButtonElement)) return;
  if (!(nextButton instanceof HTMLButtonElement)) return;

  const updateControls = () => {
    const maxScrollLeft = Math.max(0, grid.scrollWidth - grid.clientWidth);
    const hasOverflow = maxScrollLeft > SCROLL_TOLERANCE;

    controls.hidden = !hasOverflow;
    const lineWidth = progressLine.getBoundingClientRect().width || 74;

    if (!hasOverflow) {
      prevButton.disabled = true;
      nextButton.disabled = true;
      progressThumb.style.setProperty('--recently-line-thumb-width', `${lineWidth}px`);
      progressThumb.style.setProperty('--recently-line-thumb-translate', '0px');
      return;
    }

    const progress = maxScrollLeft > 0 ? grid.scrollLeft / maxScrollLeft : 0;
    const thumbWidth = Math.max(Math.min((grid.clientWidth / grid.scrollWidth) * lineWidth, lineWidth), MIN_LINE_THUMB_WIDTH);
    const thumbTravel = Math.max(lineWidth - thumbWidth, 0);

    progressThumb.style.setProperty('--recently-line-thumb-width', `${thumbWidth}px`);
    progressThumb.style.setProperty('--recently-line-thumb-translate', `${thumbTravel * progress}px`);

    prevButton.disabled = grid.scrollLeft <= SCROLL_TOLERANCE;
    nextButton.disabled = grid.scrollLeft >= maxScrollLeft - SCROLL_TOLERANCE;
  };

  const onPrevClick = () => {
    grid.scrollBy({ left: -getScrollStep(grid), behavior: 'smooth' });
  };

  const onNextClick = () => {
    grid.scrollBy({ left: getScrollStep(grid), behavior: 'smooth' });
  };

  prevButton.addEventListener('click', onPrevClick);
  nextButton.addEventListener('click', onNextClick);

  grid.addEventListener('scroll', updateControls, { passive: true });
  window.addEventListener('resize', updateControls);

  section.recentlyViewedUpdateControls = updateControls;
  section.recentlyViewedCleanupControls = () => {
    prevButton.removeEventListener('click', onPrevClick);
    nextButton.removeEventListener('click', onNextClick);
    grid.removeEventListener('scroll', updateControls);
    window.removeEventListener('resize', updateControls);
  };
  updateControls();
  section.dataset.controlsBound = 'true';
};

/** @param {RecentlyViewedSection} section */
const renderSection = async (section) => {
  const grid = section.querySelector('[data-recently-viewed-grid]');
  if (!(grid instanceof HTMLElement)) return;

  const currentHandle = section.dataset.currentHandle;
  if (!currentHandle) {
    section.hidden = true;
    return;
  }

  const minItems = Math.max(6, parseNumberAttr(section.dataset.minItems, 6));
  const configuredMaxItems = Math.max(1, parseNumberAttr(section.dataset.maxItems, 12));
  const maxItems = Math.max(minItems, configuredMaxItems);

  let handles = readHandles().filter((handle) => handle !== currentHandle);
  handles.push(currentHandle);
  handles = handles.slice(-TRACK_LIMIT);
  writeHandles(handles);

  const handlesToRender = handles.filter((handle) => handle !== currentHandle).slice(-maxItems).reverse();

  if (handlesToRender.length < minItems) {
    section.hidden = true;
    return;
  }

  // Render a stable first batch (up to 6 cards) to reduce layout jump/flicker.
  const initialRenderCount = Math.min(handlesToRender.length, Math.max(minItems, Math.min(6, maxItems)));
  const primaryCandidateCount = Math.min(handlesToRender.length, Math.max(initialRenderCount, initialRenderCount + 4));
  const primaryCandidateHandles = handlesToRender.slice(0, primaryCandidateCount);
  const remainingHandles = handlesToRender.slice(primaryCandidateCount);

  const primaryCards = await Promise.all(
    primaryCandidateHandles.map(async (handle) => {
      try {
        return await fetchCard(handle);
      } catch (error) {
        console.warn(error);
        return null;
      }
    })
  );

  const validPrimaryCardsAll = primaryCards.filter((card) => card instanceof HTMLElement);
  const validPrimaryCards = validPrimaryCardsAll.slice(0, initialRenderCount);
  if (validPrimaryCards.length < minItems) {
    section.hidden = true;
    return;
  }

  grid.replaceChildren(...validPrimaryCards);
  setupControls(section, grid);
  section.hidden = false;

  const deferredPrimaryCards = validPrimaryCardsAll.slice(initialRenderCount);
  if (remainingHandles.length === 0 && deferredPrimaryCards.length === 0) {
    return;
  }

  const remainingCards = await Promise.all(
    remainingHandles.map(async (handle) => {
      try {
        return await fetchCard(handle);
      } catch (error) {
        console.warn(error);
        return null;
      }
    })
  );

  const validRemainingCards = remainingCards.filter((card) => card instanceof HTMLElement);
  const cardsToAppend = [...deferredPrimaryCards, ...validRemainingCards];
  if (cardsToAppend.length > 0) {
    grid.append(...cardsToAppend);
    if (typeof section.recentlyViewedUpdateControls === 'function') {
      section.recentlyViewedUpdateControls();
    }
  }
};

const initRecentlyViewedProducts = () => {
  const sections = document.querySelectorAll('[data-recently-viewed-section]');
  sections.forEach((section) => {
    if (section instanceof HTMLElement) {
      renderSection(/** @type {RecentlyViewedSection} */ (section));
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewedProducts);
} else {
  initRecentlyViewedProducts();
}
